import { useLang } from "@/i18n/LangContext";
import { formatCurrency } from "@/lib/format";
import { sanitizeHtml } from "@/lib/bbcode";
import type { AppDetail } from "@/types/api";

interface GameInfoTableProps {
  app: AppDetail;
}

export default function GameInfoTable({ app }: GameInfoTableProps) {
  const { lang, t } = useLang();

  return (
    <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm mb-6 overflow-hidden">
      <div className="px-6 pt-5 pb-4">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t.gameDetail.gameInfo}</h2>
        </div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-card-border/60">
        {/* App Type */}
        <div className="flex items-center px-6 py-3">
          <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider">{t.gameDetail.type}</span>
          <span className="text-sm text-gray-900 dark:text-white font-medium capitalize">{app.type || "Game"}</span>
        </div>
        {/* App ID */}
        <div className="flex items-center px-6 py-3">
          <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider">App ID</span>
          <span className="text-sm text-gray-700 dark:text-sub-text font-mono tabular-nums">{app.steam_appid}</span>
        </div>
        {/* 개발사 */}
        {app.developers?.length > 0 && (
          <div className="flex items-center px-6 py-3">
            <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider">{t.gameDetail.developerLabel}</span>
            <span className="text-sm text-gray-900 dark:text-white font-medium">{app.developers.join(", ")}</span>
          </div>
        )}
        {/* 배급사 */}
        {app.publishers?.length > 0 && (
          <div className="flex items-center px-6 py-3">
            <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider">{t.gameDetail.publisherLabel}</span>
            <span className="text-sm text-gray-900 dark:text-white font-medium">{app.publishers.join(", ")}</span>
          </div>
        )}
        {/* 출시일 */}
        {app.release_date && (
          <div className="flex items-center px-6 py-3">
            <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider">{t.gameDetail.releaseDateLabel}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-900 dark:text-white font-medium">{app.release_date.date}</span>
              {app.release_date.coming_soon && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">{t.gameDetail.comingSoon}</span>
              )}
            </div>
          </div>
        )}
        {/* 지원 플랫폼 */}
        {app.platforms && (
          <div className="flex items-center px-6 py-3">
            <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider">{t.gameDetail.platformLabel}</span>
            <div className="flex items-center gap-3">
              {app.platforms.windows && (
                <span className="flex items-center gap-1 text-sm text-gray-900 dark:text-white">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-gray-500 dark:text-muted"><path d="M3 12.5l7-1V4L3 5.5v7zm0 7L10 21v-7.5l-7-1v7zm8-1L21 21V12h-10v6.5zm0-14v7.5H21V3L11 4.5z" /></svg>
                  Windows
                </span>
              )}
              {app.platforms.mac && (
                <span className="flex items-center gap-1 text-sm text-gray-900 dark:text-white">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-gray-500 dark:text-muted"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
                  macOS
                </span>
              )}
              {app.platforms.linux && (
                <span className="flex items-center gap-1 text-sm text-gray-900 dark:text-white">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-gray-500 dark:text-muted"><path d="M12.504 0c-.155 0-.311.015-.466.045C9.49.401 7.803 2.926 7.803 5.594c0 .724.147 1.44.436 2.119.294.687.707 1.293 1.228 1.8.112.108.224.215.337.321.113.108.227.213.34.317v3.854H7.803v1.5h2.341v3.854c-.113.104-.227.21-.34.317-.113.106-.225.213-.337.321-.521.507-.934 1.113-1.228 1.8-.289.679-.436 1.395-.436 2.119 0 2.668 1.687 5.193 4.235 5.549.155.03.311.045.466.045s.311-.015.466-.045c2.548-.356 4.235-2.881 4.235-5.549 0-.724-.147-1.44-.436-2.119-.294-.687-.707-1.293-1.228-1.8-.112-.108-.224-.215-.337-.321-.113-.107-.227-.213-.34-.317v-3.854h2.341v-1.5H14.14V10.2c.113-.104.227-.209.34-.317.113-.106.225-.213.337-.321.521-.507.934-1.113 1.228-1.8.289-.679.436-1.395.436-2.119 0-2.668-1.687-5.193-4.235-5.549A3.612 3.612 0 0012.504 0z" /></svg>
                  Linux
                </span>
              )}
            </div>
          </div>
        )}
        {/* 장르 */}
        {app.genres && app.genres.length > 0 && (
          <div className="flex items-start px-6 py-3">
            <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider pt-0.5">{t.gameDetail.genreLabel}</span>
            <div className="flex flex-wrap gap-1.5">
              {app.genres.map((g) => (
                <span key={g.id} className="text-xs bg-hover-bg dark:bg-background text-gray-700 dark:text-foreground px-2 py-1 rounded-lg font-medium">
                  {g.description}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* 지원 언어 */}
        {app.supported_languages && (
          <div className="flex items-start px-6 py-3 min-w-0">
            <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider pt-0.5">{t.gameDetail.languagesLabel}</span>
            <p className="text-xs text-gray-700 dark:text-sub-text leading-relaxed min-w-0 break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(app.supported_languages) }} />
          </div>
        )}
        {/* 가격 */}
        <div className="flex items-start px-6 py-3">
          <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider mt-0.5">{t.gameDetail.priceLabel}</span>
          <div className="flex flex-col gap-1">
            {app.is_free ? (
              <span className="text-sm text-gray-900 dark:text-white font-medium">{t.gameDetail.free}</span>
            ) : app.package_groups && app.package_groups.some(g => (g.subs?.length ?? 0) > 1) ? (
              app.package_groups.flatMap(g => g.subs || []).map((sub) => (
                <div key={sub.packageid} className="flex items-center gap-2">
                  <span className="text-sm text-gray-900 dark:text-white font-medium tabular-nums">
                    {sub.is_free_license ? t.gameDetail.free : sub.price_in_cents_with_discount > 0 ? formatCurrency(sub.price_in_cents_with_discount / 100, undefined, lang) : t.gameDetail.free}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-sub-text">
                    {sub.option_text?.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim()}
                  </span>
                </div>
              ))
            ) : (
              <span className="text-sm text-gray-900 dark:text-white font-medium">
                {app.price_overview ? app.price_overview.final_formatted : t.gameDetail.noPriceInfo}
              </span>
            )}
          </div>
        </div>
        {/* Metacritic */}
        {app.metacritic && (
          <div className="flex items-center px-6 py-3">
            <span className="w-32 shrink-0 text-xs font-semibold text-gray-400 dark:text-sub-text uppercase tracking-wider">Metacritic</span>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                app.metacritic.score >= 75 ? "bg-emerald-500 text-white" :
                app.metacritic.score >= 50 ? "bg-amber-400 text-gray-900" :
                "bg-red-500 text-white"
              }`}>
                {app.metacritic.score}
              </span>
              <span className="text-xs text-gray-500 dark:text-muted">
                Metacritic
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
